import { uuid } from '@coxy/utils'
import { addItemToast, hideItemToast, removeAllToasts, TIMEOUT, type Toast } from '@shared-core/features/notifications'
import { NotificationsContext } from '@shared-core/features/notifications/context'
import { useAppDispatch } from '@shared-core/store'
import { type PropsWithChildren, useCallback } from 'react'

export function NotificationsProvider({ children }: PropsWithChildren) {
  const dispatch = useAppDispatch()

  const removeAll = useCallback(() => dispatch(removeAllToasts()), [dispatch])
  const removeToast = useCallback((id: string) => dispatch(hideItemToast(id)), [dispatch])
  const addToast = useCallback(
    (el: Omit<Toast, 'uuid'>) => {
      const element = { ...el, uuid: uuid(5) }
      dispatch(addItemToast(element))

      return element.uuid
    },
    [dispatch],
  )

  const addToastError = useCallback(
    (text: string) =>
      addToast({
        type: 'error',
        text,
        timeout: TIMEOUT.DEFAULT,
        override: true,
        isClosable: true,
      }),
    [addToast],
  )

  const addToastSuccess = useCallback(
    (text: string) =>
      addToast({
        type: 'success',
        text,
        timeout: TIMEOUT.DEFAULT,
        override: true,
        isClosable: true,
      }),
    [addToast],
  )

  const addToastInfo = useCallback(
    (text: string) =>
      addToast({
        type: 'info',
        text,
        timeout: TIMEOUT.DEFAULT,
        override: true,
        isClosable: true,
      }),
    [addToast],
  )

  const addToastProcess = useCallback(
    (text: string) =>
      addToast({
        text,
        timeout: TIMEOUT.LONG,
        isClosable: false,
        override: true,
      }),
    [addToast],
  )

  return (
    <NotificationsContext.Provider
      value={{
        addToastInfo,
        removeToast,
        addToast,
        removeAllToasts: removeAll,
        addToastError,
        addToastSuccess,
        addToastProcess,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  )
}
